import { useState, useEffect, useCallback } from 'react';
import { userApi } from '@/api/user';
import { trackEvent } from '@/api/track';
import { POINT_BALANCE_CHANGED_EVENT } from '@/lib/creditBalance';

const getErrorCode = (error: unknown) => {
  const maybeError = error as {
    code?: string | number;
    message?: string;
    response?: { status?: number; data?: { code?: string | number; message?: string } };
  };
  return String(
    maybeError.response?.data?.code ??
      maybeError.response?.status ??
      maybeError.code ??
      maybeError.message ??
      'unknown',
  );
};

type PointAccountTrackingSource = 'usage_balance' | 'account_menu_balance';

interface UsePointAccountOptions {
  enabled?: boolean;
  trackingSource?: PointAccountTrackingSource;
}

const POINT_ACCOUNT_EVENT_CODE: Record<PointAccountTrackingSource, string> = {
  usage_balance: 'usage_balance_load_result',
  account_menu_balance: 'account_menu_balance_load_result',
};

const POINT_ACCOUNT_SOURCE: Record<PointAccountTrackingSource, string> = {
  usage_balance: 'balance',
  account_menu_balance: 'menu_balance',
};

export const usePointAccount = (options: boolean | UsePointAccountOptions = true) => {
  const enabled = typeof options === 'boolean' ? options : (options.enabled ?? true);
  const trackingSource =
    typeof options === 'boolean' ? 'usage_balance' : (options.trackingSource ?? 'usage_balance');
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [refreshToken, setRefreshToken] = useState(0);
  const refresh = useCallback(() => {
    setLoading(true);
    setRefreshToken((value) => value + 1);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) return;

    const handleBalanceChanged = () => refresh();
    window.addEventListener(POINT_BALANCE_CHANGED_EVENT, handleBalanceChanged);

    return () => {
      window.removeEventListener(POINT_BALANCE_CHANGED_EVENT, handleBalanceChanged);
    };
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const startTime = performance.now();

    userApi
      .getPointAccount()
      .then((account) => {
        if (cancelled) return;
        const nextBalance = account?.balance ?? 0;
        setBalance(nextBalance);
        trackEvent({
          eventCode: POINT_ACCOUNT_EVENT_CODE[trackingSource],
          eventData: JSON.stringify({
            result: 'success',
            source: POINT_ACCOUNT_SOURCE[trackingSource],
            balance: nextBalance,
            durationMs: Math.round(performance.now() - startTime),
            errorCode: null,
          }),
        });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setBalance(null);
          trackEvent({
            eventCode: POINT_ACCOUNT_EVENT_CODE[trackingSource],
            eventData: JSON.stringify({
              result: 'failed',
              source: POINT_ACCOUNT_SOURCE[trackingSource],
              balance: null,
              durationMs: Math.round(performance.now() - startTime),
              errorCode: getErrorCode(error),
            }),
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, refreshToken, trackingSource]);

  return {
    balance: enabled ? balance : null,
    loading: enabled ? loading : false,
    refresh,
  };
};
